import Axios, { AxiosRequestConfig } from 'axios';
import { axiosInstance } from "../../axios-instance";
import { AppState, AppThunkDispatch } from '..';
import { createChunks, parametersAsObject } from '../../globals/utils';
import FileStorage from '../../services/file-storage';
import { BaseSubsctiptionPlanParameters, CompleteUpload, ConfigManagementRequestPayload, PanelConfigPostPayload, QueueConfigRequestPayload, RegisterArguments } from '../../services/types';
import ConfigurationFilesLibrary from '../../services/config-files-library';
import { enqueueSnackbarAction } from '../snackbar/actions';
import {
  CommunicatorFilesRecord,
  LibraryManagementActionNames,
  SoftwareFilesRecord,
  VimsRtdsTableDataItem,
} from './types';
import { QueueSubscriptionRequest } from '../../entities/subscription-config-v1/queue-subscription-request';
import { RemoteFlashActionNames } from '../configure/remote-flash/types';
import SubscriptionCatalog from '../../services/subscription-catalog';
import ConfigurationManagement from '../../services/config-management';
import ManualOnboardingService from '../../services/asset-management';
import PanelConfigurationManagement from '../../services/panel-configuration';


export const uploadSoftwareFiles =
  ({
    file,
    fileTypeId,
    partNumber,
    finishedCallback,
    overwriteCallback = () => { },
    setPercentUpload,
    isRetrievable = true,
    overwrite,
  }: {
    file: File;
    fileTypeId: string;
    finishedCallback: Function;
    overwriteCallback?: Function;
    setPercentUpload: Function;
    partNumber: string | undefined;
    isRetrievable?: boolean;
    overwrite?: boolean;
  }) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const { storageV1BaseUrl } = envVariables;
      const clientAuth: any = client?.authenticate();
      const userId = clientAuth.loginId;
      const storage = new FileStorage(client, storageV1BaseUrl);
      const chunks = createChunks(file, 10);
      const numberOfParts = chunks.length;
      const partyNumber = 'CAT';
      const registrationInputs: RegisterArguments = {
        queryParameters: {
          numberOfParts,
          partNumber,
          partyNumber: 'CAT',
          fileName: file.name,
          origin: 'iot-gateway',
          ttl: 86400,
          contentType: 'application/bin',
        },
      };
      try {
        let percentUpload = 10;
        setPercentUpload(percentUpload);
        const {
          data: { uploadUrls, id, uploadId },
        } = await storage.multiPartRegister(registrationInputs);
        const uploadMultiple = () =>
          uploadUrls.map(async (uploadUrl, iterator) => {
            const uploadPercentage = 80 / uploadUrls.length;
            const url = uploadUrl.split('?');
            const parameterString: URLSearchParams | any = new URLSearchParams(url[1]);
            const urlParameters = parametersAsObject(parameterString);
            const request: AxiosRequestConfig = {
              url: url[0],
              params: urlParameters,
              method: 'PUT',
              data: chunks[iterator],
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            };
            const uploadResponse = await Axios(request);
            percentUpload += uploadPercentage;
            setPercentUpload(percentUpload);
            return uploadResponse;
          });

        await Promise.all(uploadMultiple());

        const completionInput: CompleteUpload = {
          queryParameters: {
            partyNumber,
            uploadId,
            id,
            partNumber,
          },
        };
        const {
          data,
          data: { fileName, uploadTime },
        } = await storage.multiPartCompleteUpload(completionInput);

        percentUpload += 10;
        setPercentUpload(percentUpload);

        const parameters = new URLSearchParams();
        parameters.append('fileId', data.id);
        parameters.append(
          'metadata',
          JSON.stringify({
            isRetrievable,
            id,
            fileTypeId,
            fileName,
            partNumber,
            userId,
            overwrite,
            applicationId: 'iot-gateway',
          }),
        );
        parameters.append('overwrite', `${overwrite}`);
        const configure = new ConfigurationFilesLibrary(
          client,
          envVariables.configurationFilesLibraryV1BaseUrl,
        );

        const configureResponse = await configure.uploadConfigFilesFromStorage(parameters);
        percentUpload += 10;
        setPercentUpload(percentUpload);
        setPercentUpload(100);
        if (configureResponse.status === 200 || configureResponse.status === 201) {
          setPercentUpload(0);
          finishedCallback({
            ...configureResponse.data,
            createdTime: uploadTime,
          });
        }
      } catch (uploadError: any) {
        setPercentUpload(0);
        let message = 'There was an upload error. Please try again';
        if (uploadError?.response?.status === 409) {
          if (uploadError.response.data.isRetrievable) {
            message = 'File already exists in the database';
          }
          else {
            message = '';
          }
          dispatch({
            type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
            payload: { hash: uploadError.response.data.id },
          });
          overwriteCallback();
        } else if (uploadError?.response?.status === 403) {
          message = 'Permission denied';
        } else if (uploadError?.response?.status === 500) {
          message = 'Internal server error';
        }
        if (message) {
          dispatch(
            enqueueSnackbarAction({
              message,
              options: {
                key: (Date.now() + Math.random()).toString(),
                variant: 'error',
              },
            }),
          );
        }
      }
    };

export const updatePartNumber =
  (row: any, payload: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
      libraryManagement: { softwareFiles },
    } = getState();
    const filesLibrary = new ConfigurationFilesLibrary(
      client,
      envVariables.configurationFilesLibraryV1BaseUrl,
    );
    try {
      const response = await filesLibrary.updatePartNumber(row.original.id, payload);
      if (response.data.partNumber !== payload.partNumber) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Part number could not be saved',
            options: {
              key: (Date.now() + Math.random()).toString(),
              variant: 'warning',
            },
          }),
        );
      } else {
        const index = softwareFiles.findIndex((file) => file.id === row.original.id);
        softwareFiles.splice(index, 1, {
          ...row.original,
          ...payload,
        });
        dispatch(
          enqueueSnackbarAction({
            message: 'Part number updated successfully',
            options: {
              key: (Date.now() + Math.random()).toString(),
              variant: 'success',
            },
          }),
        );
        dispatch({
          type: LibraryManagementActionNames.SET_SOFTWARE_FILES,
          payload: softwareFiles,
        });
      }
      return response.data.partNumber;
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response.data?.status || 'Something went wrong. Please try again later',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'error',
          },
        }),
      );
      return payload.partNumber;
    }
  };

export const removeSelectedRows =
  (
    allRows: Array<SoftwareFilesRecord | CommunicatorFilesRecord>,
    selectedRows: Array<SoftwareFilesRecord | CommunicatorFilesRecord>,
    callbackFunction: Function = () => { },
  ) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      if (allRows) {
        const configure = new ConfigurationFilesLibrary(
          client,
          envVariables.configurationFilesLibraryV1BaseUrl,
        );
        const deletedIds: Array<string> = [];
        await Promise.allSettled(
          selectedRows.map((row) =>
            configure
              .deleteConfigFile(row.id)
              .then((success) => {
                if (success.status === 204) {
                  deletedIds.push(row.id);
                }
                return true;
              })
              .catch((error) => {
                const errorData = error.response.data
                const errorMessage = !errorData.status && errorData.code === '429.004' ?
                  'Too many requests, unable to delete some files' : errorData.status
                dispatch(
                  enqueueSnackbarAction({
                    message: errorMessage,
                    options: {
                      key: (Date.now() + Math.random()).toString(),
                      variant: 'error',
                    },
                  }),
                );
              }),
          ),
        );
        if (deletedIds.length > 0) {
          dispatch(
            enqueueSnackbarAction({
              message: 'File deleted successfully',
              options: {
                key: (Date.now() + Math.random()).toString(),
                variant: 'success',
              },
            }),
          );
        }
        callbackFunction(deletedIds);
      }
    };

export const associateCommunicatorFileSubscription =
  (subscriptionId: string, payload: object) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      try {
        if (subscriptionId) {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axiosInstance.patch<QueueSubscriptionRequest>(
            `${envVariables.subscriptionCatalogV1BaseUrl}/subscriptions/${subscriptionId}`,
            payload,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/merge-patch+json',
              },
            },
          );
          if (response.status !== 200) {
            dispatch(
              enqueueSnackbarAction({
                message: 'Association of communicator file to subscription failed',
                options: {
                  key: (Date.now() + Math.random()).toString(),
                  variant: 'error',
                },
              }),
            );
          }
        }
      } catch (error) {
        dispatch(
          enqueueSnackbarAction({
            message: 'Operation failed',
            options: { key: (Date.now() + Math.random()).toString(), variant: 'error' },
          }),
        );
      }
    };


export const handleSubscriptionCatalogSync = (userId?: string, applicationId?: string): any => (
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const { login: { client, envVariables } } = getState();
    const serviceSubscriptionCatalog = new SubscriptionCatalog(client, envVariables.subscriptionCatalogV1BaseUrl);
    try {
      const response = await serviceSubscriptionCatalog.postSync(
        {
          userId: userId || undefined,
          applicationId: applicationId || undefined,
        }
      );
      const { users } = response.data;
      if (users && users.length) {
        dispatch(
          enqueueSnackbarAction({
            message: "Subscription Catalog Sync is Successfull",
            options: { variant: 'success' },
          }),
        );
        return users[0];
      }
      if (response.status === 202) {
        dispatch(
          enqueueSnackbarAction({
            message: "Subscription Catalog Sync is Successfull",
            options: { variant: 'success' },
          }),
        );
        return true;
      }
      throw new Error("Subscription Catalog Sync is Failed");
      return false;
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response?.data?.description || "Subscription Catalog Sync is Failed",
          options: { variant: 'error' },
        }),
      );
      return false;
    }
  }
)

let vimsSha:any='';
export const uploadVimsConfigFile =
  ({
    file,
    fileName,
    fileTypeId,
    finishedCallback,
    setPercentUpload,
    isRetrievable = true,
    overwrite,
  }: {
    file: File;
    fileName:string;
    fileTypeId: string;
    finishedCallback: Function;
    setPercentUpload: Function;
    isRetrievable?: boolean;
    overwrite?: boolean;
  }) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
     vimsSha='';
      const {
        login: { client, envVariables },
      } = getState();
      const { storageV1BaseUrl } = envVariables;
      const clientAuth: any = client?.authenticate();
      const userId = clientAuth.loginId;
      const storage = new FileStorage(client, storageV1BaseUrl);
      const chunks = createChunks(file, 10);
      const numberOfParts = chunks.length;
      const partyNumber = 'CAT';
      const registrationInputs: RegisterArguments = {
        queryParameters: {
          numberOfParts,
          partyNumber: 'CAT',
          fileName,
          origin: 'iot-gateway',
          ttl: 86400,
          contentType: 'application/bin',
        },
      };
     console.log(file);
      try {
        let percentUpload = 10;
        setPercentUpload(percentUpload);
        const {
          data: { uploadUrls, id, uploadId },
        } = await storage.multiPartRegister(registrationInputs);
        const uploadMultiple = () =>
          uploadUrls.map(async (uploadUrl, iterator) => {
            const uploadPercentage = 80 / uploadUrls.length;
            const url = uploadUrl.split('?');
            const parameterString: URLSearchParams | any = new URLSearchParams(url[1]);
            const urlParameters = parametersAsObject(parameterString);
            const request: AxiosRequestConfig = {
              url: url[0],
              params: urlParameters,
              method: 'PUT',
              data: chunks[iterator],
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            };
            const uploadResponse = await Axios(request);
            percentUpload += uploadPercentage;
            setPercentUpload(percentUpload);
            return uploadResponse;
          });
         console.log(registrationInputs);
        await Promise.all(uploadMultiple());

        const completionInput: CompleteUpload = {
          queryParameters: {
            partyNumber,
            uploadId,
            id,
          },
        };
        const {
          data,
          data: { fileName, uploadTime },
        } = await storage.multiPartCompleteUpload(completionInput);

        percentUpload += 10;
        setPercentUpload(percentUpload);

        const parameters = new URLSearchParams();
        parameters.append('fileId', data.id);
        parameters.append(
          'metadata',
          JSON.stringify({
            isRetrievable,
            id,
            fileTypeId,
            fileName,
            userId,
            overwrite,
            applicationId: 'iot-gateway',
          }),
        );
        parameters.append('overwrite', `${overwrite}`);
        const configure = new ConfigurationFilesLibrary(
          client,
          envVariables.configurationFilesLibraryV1BaseUrl,
        );

        const configureResponse = await configure.uploadConfigFilesFromStorage(parameters);
        percentUpload += 10;
        setPercentUpload(percentUpload);
        setPercentUpload(100);
        if (configureResponse.status === 200 || configureResponse.status === 201) {
         vimsSha = configureResponse.data.id;
          setPercentUpload(0);
          finishedCallback({
            ...configureResponse.data,
            createdTime: uploadTime,
          });
          console.log(vimsSha);
        }        
      } catch (uploadError: any) { 
        if (uploadError?.response?.status === 409){
        dispatch({
          type: LibraryManagementActionNames.SET_VIMS_FILE_EXISTS,
          payload: { hash: uploadError.response.data.id },
        });
        setPercentUpload(0);
        vimsSha = uploadError.response.data.id;
        console.log(uploadError.response.data.id);
        }
        finishedCallback({
          error: true,
          details: uploadError?.response?.data || uploadError,
        });
      }
    };

let rtdsSha='';
export const uploadRtdsConfigFile =
  ({
    file,
    fileTypeId,
    partNumber,
    finishedCallback,
    setPercentUpload,
    isRetrievable = true,
    overwrite,
  }: {
    file: File;
    fileTypeId: string;
    finishedCallback: Function;
    setPercentUpload: Function;
    partNumber: string | undefined;
    isRetrievable?: boolean;
    overwrite?: boolean;
  }) =>
    async (dispatch: AppThunkDispatch, getState: () => AppState) => {
      const {
        login: { client, envVariables },
      } = getState();
      const { storageV1BaseUrl } = envVariables;
      const clientAuth: any = client?.authenticate();
      const userId = clientAuth.loginId;
      const storage = new FileStorage(client, storageV1BaseUrl);
      const chunks = createChunks(file, 10);
      const numberOfParts = chunks.length;
      const partyNumber = 'CAT';
      const registrationInputs: RegisterArguments = {
        queryParameters: {
          numberOfParts,
          partNumber,
          partyNumber: 'CAT',
          fileName: file.name,
          origin: 'iot-gateway',
          ttl: 86400,
          contentType: 'application/bin',
        },
      };
      try {
        let percentUpload = 10;
        setPercentUpload(percentUpload);
        const {
          data: { uploadUrls, id, uploadId },
        } = await storage.multiPartRegister(registrationInputs);
        const uploadMultiple = () =>
          uploadUrls.map(async (uploadUrl, iterator) => {
            const uploadPercentage = 80 / uploadUrls.length;
            const url = uploadUrl.split('?');
            const parameterString: URLSearchParams | any = new URLSearchParams(url[1]);
            const urlParameters = parametersAsObject(parameterString);
            const request: AxiosRequestConfig = {
              url: url[0],
              params: urlParameters,
              method: 'PUT',
              data: chunks[iterator],
              headers: {
                'Content-Type': 'application/octet-stream',
              },
            };
            const uploadResponse = await Axios(request);
            percentUpload += uploadPercentage;
            setPercentUpload(percentUpload);
            return uploadResponse;
          });

        await Promise.all(uploadMultiple());

        const completionInput: CompleteUpload = {
          queryParameters: {
            partyNumber,
            uploadId,
            id,
            partNumber,
          },
        };
        const {
          data,
          data: { fileName, uploadTime },
        } = await storage.multiPartCompleteUpload(completionInput);

        percentUpload += 10;
        setPercentUpload(percentUpload);

        const parameters = new URLSearchParams();
        parameters.append('fileId', data.id);
        parameters.append(
          'metadata',
          JSON.stringify({
            isRetrievable,
            id,
            fileTypeId,
            fileName,
            partNumber,
            userId,
            overwrite,
            applicationId: 'iot-gateway',
          }),
        );
        parameters.append('overwrite', `${overwrite}`);
        const configure = new ConfigurationFilesLibrary(
          client,
          envVariables.configurationFilesLibraryV1BaseUrl,
        );

        const configureResponse = await configure.uploadConfigFilesFromStorage(parameters);
        percentUpload += 10;
        setPercentUpload(percentUpload);
        setPercentUpload(100);
        if (configureResponse.status === 200 || configureResponse.status === 201) {
          rtdsSha = configureResponse.data.id;
          setPercentUpload(0);
          finishedCallback({
            ...configureResponse.data,
            createdTime: uploadTime,
          });
        }
      } catch (uploadError: any) {
        dispatch({
          type: LibraryManagementActionNames.SET_RTDS_FILE_EXISTS,
          payload: { hash: uploadError.response.data.id },
        });
        setPercentUpload(0);
        rtdsSha = uploadError.response.data.id;
        console.log(uploadError.response.data.id);
         finishedCallback({
          error: true,
          details: uploadError?.response?.data || uploadError,
        });
      }

    };
let id='';
export const queueConfigManagement = ({
  generationType,
  vimsFileName,
  vimsSha1,
  isReboot,
  subscriptionName,
  isDefault,
  finishedCallback =() =>{ },
  overwriteCallback = () => { },
  isOverride = false,
}: {
  generationType: string;
  vimsFileName: string;
  vimsSha1:string;
  finishedCallback?: Function;
  overwriteCallback?: Function;
  partNumber: string | undefined;
  subscriptionName: string;
  isReboot: boolean;
  isDefault: boolean
  isOverride?: boolean;
}) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const { login: { client, envVariables } } = getState();
    const clientAuth: any = client?.authenticate();
    const userId = clientAuth?.loginId;
    const configurationManagement = new ConfigurationManagement(client, envVariables.configManangementV1BaseUrl);

    const queueConfigRequest: QueueConfigRequestPayload = {
      configs: [
        {
          generationType,
          vimsSha1,
          vimsFileName,
          isReboot,
          configurations: [
            {
              subscriptionName: generationType !== 'PL444' ? subscriptionName : undefined,
              isDefault,
              rtdsSha1: generationType !== 'PL444' ? rtdsSha : undefined,
            },
          ],
        },
      ],
      userId,
      applicationId: 'iot-gateway',
    }
    console.log(queueConfigRequest);
    const headers = {
      isOverride,
    };
   // queueConfigRequest.configs[0].isOverride = isOverride;
    if (isOverride === true)
      queueConfigRequest.configs[0].configurations[0].configId = id;
    if(!queueConfigRequest.configs[0].vimsSha1){
          queueConfigRequest.configs[0].vimsSha1=vimsSha
         }
    try {
      const response = await configurationManagement.queueAssetDeviceConfiguration(queueConfigRequest,headers);
         finishedCallback({
           ...response.data,
         });
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs queued successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );

    } catch (error: any) {
      let message = 'There was an upload error. Please try again';
      if (error?.response?.status === 409) {
         console.log(error.response);
         id=error.response.data.configs[0].configurations[0].configId
        if (error.response.data.isRetrievable) {
          message = 'File already exists in the database';
        }
        else {
          message = '';
        }
        overwriteCallback();
      } else if (error?.response?.status === 403) {
        message = 'Permission denied';
      } else if (error?.response?.status === 500) {
        message = 'Internal server error';
      }
      if (message) {
        dispatch(
          enqueueSnackbarAction({
            message,
            options: {
              key: (Date.now() + Math.random()).toString(),
              variant: 'error',
            },
          }),
        );
      }
    }
  };

export const patchConfigManagement = (input: any) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const { login: { client, envVariables } } = getState();
    const configurationManagement = new ConfigurationManagement(client, envVariables.configManangementV1BaseUrl);

    console.log(input);
    const patchInputs={...input}
   if(patchInputs.configs[0].configurations[0].rtdsSha1===''){
    patchInputs.configs[0].configurations[0].rtdsSha1 = rtdsSha;
   }
    try {
      const response = await configurationManagement.updateAssetDeviceConfiguration(patchInputs);
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs updated successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );

    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Error while updatings',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'error',
          },
        }),
      );
    }
  };

export const deleteConfigManagement =
  (deleteConfigInput: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const configurationManagement = new ConfigurationManagement(
      client,
      envVariables.configManangementV1BaseUrl,
    );
    console.log(deleteConfigInput);
    const deleteInputs = { ...deleteConfigInput };
    try {
      const response = await configurationManagement.deleteAssetDeviceConfiguration(deleteInputs);
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs deleted successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );

    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Error while deleting',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'error',
          },
        }),
      );
    }
  };
export const getBaseSubscriptionManagement =
  (input: BaseSubsctiptionPlanParameters) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
    const assetManagment = new ManualOnboardingService(client, envVariables.assetManagementBaseURL);
    const response = await assetManagment.getBaseSubsctiptionPlans(input);
    const {plans} = response.data;
    return plans || [];
  };

export const setVimsRtdsTableData = (data: VimsRtdsTableDataItem[]) => ({
  type: 'SET_VIMS_RTDS_TABLE_DATA',
  payload: data,
});

export const fetchVimsRtdsTableData = (generationType: string) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
  const { client, envVariables } = getState().login;
  const configurationManagement = new ConfigurationManagement(client, envVariables.configManangementV1BaseUrl);
  const input = { generationType };
  try {
    const result = await configurationManagement.getConfigurationFiles(input);
    const configData = result?.data?.configs;
    let tableData: VimsRtdsTableDataItem[] = [];
    if (Array.isArray(configData) && configData.length > 0) {
      tableData = configData.flatMap((config: any) =>
        config.configurations.map((subConfig: any) => ({
          id: config.vimsSha1,
          generationType: config.generationType,
          vimsFileName: config.vimsFileName,
          vimsSha1: config.vimsSha1,
          fileName: config.vimsFileName,
          isReboot: config.isReboot,
          rtdsSha1: subConfig.rtdsSha1,
          vimsConfigId: config.vimsConfigId,
          configId: subConfig.configId,
          isDefault: subConfig.isDefault,
          subscriptionName: subConfig.subscriptionName,
          updatedTS: subConfig.updatedTS,
          updatedBy: subConfig.updatedBy,
        }))
      );
    }
    dispatch(setVimsRtdsTableData(tableData));
  } catch (error) {
    dispatch(setVimsRtdsTableData([]));
  }
};

export const queuePanelConfigManagement = ({
  generationType,
  vimsFileName,
  vimsSha1,
  panelName,
  isDefault,
  vimsConfigId,
  finishedCallback = () => { } ,
}: {
  generationType: string;
  vimsFileName: string;
  vimsSha1:string;
  panelName: string;
  isDefault: boolean;
  vimsConfigId:string;
  finishedCallback?: Function;  
}) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const { login: { client, envVariables } } = getState();
    const clientAuth: any = client?.authenticate();
    const userId = clientAuth?.loginId;
    const panelConfigurationManagement = new PanelConfigurationManagement(client, envVariables.panelConfigurationV1BaseUrl);

    const queueConfigRequest: PanelConfigPostPayload  = {
      configs: [
        {
          generationType,
          vimsSha1,
          vimsFileName,
          panelName,
          isDefault,
          vimsConfigId,
        },
      ],
      userId,
      applicationId: 'iot-gateway',
    }
    try {
      const response = await panelConfigurationManagement.queuePanelConfiguration(queueConfigRequest);
         finishedCallback({
           ...response.data,
         });
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs queued successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );

    } catch (error: any) {
      let message = error.response?.data?.description || 'There was an upload error. Please try again';
      if (error?.response?.status === 409) {
         console.error(error.response);
          message = 'File already exists in the database';
      } else if (error?.response?.status === 403) {
        message = 'Permission denied';
      } else if (error?.response?.status === 500) {
        message = 'Internal server error';
      }
      if (message) {
        dispatch(
          enqueueSnackbarAction({
            message,
            options: {
              key: (Date.now() + Math.random()).toString(),
              variant: 'error',
            },
          }),
        );
      }
    }
  };

export const patchPanelConfigManagement = (input: any) =>
  async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const { login: { client, envVariables } } = getState();
    const panelConfigurationManagement = new PanelConfigurationManagement(client, envVariables.panelConfigurationV1BaseUrl);
    const patchInputs={...input}
    try {
      const response = await panelConfigurationManagement.updatePanelConfiguration(patchInputs);
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs updated successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );
      return response;
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response?.data?.description ||  'Error while updating',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'error',
          },
        }),
      );
      return error?.response;
    }
  };

export const deletePanelConfigManagement =
  (deleteConfigInput: any) => async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const {
      login: { client, envVariables },
    } = getState();
   const panelConfigurationManagement = new PanelConfigurationManagement(client, envVariables.panelConfigurationV1BaseUrl);
    const deleteInputs = { ...deleteConfigInput };
    try {
      const response = await panelConfigurationManagement.deletePanelConfiguration(deleteInputs);
      dispatch(
        enqueueSnackbarAction({
          message: 'Configs deleted successfully',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'success',
          },
        }),
      );
     return response;
    } catch (error: any) {
      dispatch(
        enqueueSnackbarAction({
          message: error.response?.data?.description ||  'Error while deleting',
          options: {
            key: (Date.now() + Math.random()).toString(),
            variant: 'error',
          },
        }),
      );
      return error?.response;
    }
  };